import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import useResponsive from '../../../hooks/useResponsive';
import { useOnboardingData } from '@/hooks/useOnboardingData';
// import Spinner from '../../../components/Spinner';
import LandingPageSkeleton from '../../../components/LandingPageSkeleton';

import ToastMessage, { ToastMessageProps } from '../../../components/ToastMessage';
import JobDetailsModal from '../../../components/JobDetailsModal';
import ExplainerVideo from '../../../components/ExplainerVideo';
import TermsCheckbox from '../../../components/TermsCheckbox';
import GradientButton from '../../../components/GradientButton';
import WatchVideoButton from '../../../components/WatchVideoButton';
import InterviewTipsModal from './InterviewTipsModal';
import ExitIntentModal from '../../../components/ExitIntentModal';
import ResumeInterviewModal from '../../../components/ResumeInterviewModal';

import { getWithExpiry, setWithExpiry } from '../../../utils/sessionStorage';
import { pushScreenerStage, pushExitIntent } from '../../../utils/analytics';
import { markInteractedThisSession } from '../../../utils/exitIntentStorage';
import useExitIntentTrigger from '../../../hooks/useExitIntentTrigger';
import { scheduleReminder } from '../../../api/ReminderService';
import { EXIT_INTENT_LANDING_TIMINGS } from '../../../utils/exitIntentConfig';
import {
  logScreenerLandingPageViewed,
  logScreenerCtaClicked,
  SCREENER_STARTED_AT_KEY,
} from '../../../utils/statsig';
import { getDeviceType } from '../../../utils/getDeviceType';
import { getScreenerSource } from '../../../utils/getScreenerSource';
import { useScreenerAbandonTracker } from '../../../hooks/useScreenerAbandonTracker';

import onboardingData from '../../../data/mockOnboard.json';

const MINUTES_PER_QUESTION = 5;

const LandingPage = () => {
  const { applicantGuid } = useParams();
  const isMobile = useResponsive('md');
  const navigate = useNavigate();
  const isLargeDesktops = window.innerWidth >= 1920;
  const isShortViewport = window.innerHeight <= 663;

  const {
    job,
    isLoading,
    error,
    hasQuestionnaire,
    contentType,
    ttsLoading,
    isConnected,
    questionCount,
    answeredCount,
    hasPartialProgress,
  } = useOnboardingData(applicantGuid);

  const termsErrorRef = useRef<HTMLDivElement>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [shakeButton, setShakeButton] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [showExplainerVideo, setShowExplainerVideo] = useState(false);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastMessageProps>({
    hasTitle: false,
    title: '',
    message: '',
    type: 'info',
    isMobile,
    isOpen: false,
  });

  const companyName = job?.companyName || '';
  const jobTitle = job?.title || '';
  const location = job?.locations?.[0]
    ? `${job.locations[0].city}, ${job.locations[0].region}, ${job.locations[0].country}`
    : '';
  const estimatedDuration = questionCount * MINUTES_PER_QUESTION;

  useEffect(() => {
    if (!isConnected) {
      setToast({
        hasTitle: true,
        title: 'Connection Lost',
        message: 'You are offline. Please reconnect to continue.',
        type: 'error',
        isMobile,
        isOpen: true,
      });
      setShowToast(true);
    } else {
      setShowToast(false);
    }
  }, [isConnected, isMobile]);

  useEffect(() => {
    if (error) {
      setToast({
        hasTitle: true,
        title: 'Error',
        message: error.replace(/ \(\d+\)$/, ''),
        type: 'error',
        isMobile,
        isOpen: true,
      });
      setShowToast(true);
    }
  }, [error, isMobile]);

  useEffect(() => {
    const anyModalOpen = showTipsModal || showJobDetails || showExplainerVideo;
    if (anyModalOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showTipsModal, showJobDetails, showExplainerVideo]);

  const hasPushedStage = useRef(false);
  useEffect(() => {
    if (!isLoading && hasQuestionnaire && !hasPushedStage.current) {
      hasPushedStage.current = true;
      const jobId = getWithExpiry('jobId') || '';
      const companyId = getWithExpiry('companyId') || '';
      pushScreenerStage({
        stage_name: 'landing_page',
        job_id: jobId,
        applicant_guid: applicantGuid || '',
        screener_question_count: questionCount,
      });

      setWithExpiry(SCREENER_STARTED_AT_KEY, Date.now(), 1000 * 60 * 60 * 24);

      logScreenerLandingPageViewed({
        jobId,
        companyId,
        applicantGuid: applicantGuid || undefined,
        deviceType: getDeviceType(),
        source: getScreenerSource(),
      });
    }
  }, [isLoading, hasQuestionnaire, applicantGuid, questionCount]);

  useScreenerAbandonTracker({ lastStep: 'landing_page', isCompleted: isNavigating });

  // VI-594: resume modal shows on landing when the candidate has partial
  // upstream progress (status Pending, answers.length > 0 from
  // useOnboardingData). It must coexist with the exit-intent modal — we
  // suppress exit-intent while this is up to avoid stacking.
  const showResumeModal =
    hasPartialProgress && !isLoading && !error && !isNavigating;

  const otherModalOpen =
    showTipsModal || showJobDetails || showExplainerVideo || showResumeModal;
  const exitIntentSuspended =
    !hasQuestionnaire ||
    isLoading ||
    isNavigating ||
    !!error ||
    otherModalOpen;

  const {
    isOpen: showExitIntentModal,
    triggerSource: exitIntentTriggerSource,
    dismiss: dismissExitIntent,
  } = useExitIntentTrigger({
    page: 'landing',
    applicantGuid: applicantGuid || '',
    mouseLeaveDelayMs: EXIT_INTENT_LANDING_TIMINGS.mouseLeaveDelayMs,
    idleSeconds: EXIT_INTENT_LANDING_TIMINGS.idleSeconds,
    suspended: exitIntentSuspended,
  });

  const hasReportedExitIntentShown = useRef(false);
  useEffect(() => {
    if (showExitIntentModal && exitIntentTriggerSource && !hasReportedExitIntentShown.current) {
      hasReportedExitIntentShown.current = true;
      pushExitIntent({
        page: 'landing',
        trigger: exitIntentTriggerSource,
        action: 'shown',
        applicant_guid: applicantGuid || '',
        job_id: getWithExpiry('jobId') || '',
      });
    }
    if (!showExitIntentModal) {
      hasReportedExitIntentShown.current = false;
    }
  }, [showExitIntentModal, exitIntentTriggerSource, applicantGuid]);

  const handleExitIntentStartInterview = () => {
    pushExitIntent({
      page: 'landing',
      trigger: exitIntentTriggerSource ?? 'idle',
      action: 'start_interview',
      applicant_guid: applicantGuid || '',
      job_id: getWithExpiry('jobId') || '',
    });
    markInteractedThisSession(applicantGuid || '', 'landing');
    dismissExitIntent();
    void handleStartInterview();
  };

  const handleExitIntentSendReminder = () => {
    const trigger = exitIntentTriggerSource ?? 'idle';
    pushExitIntent({
      page: 'landing',
      trigger,
      action: 'send_reminder',
      applicant_guid: applicantGuid || '',
      job_id: getWithExpiry('jobId') || '',
    });
    markInteractedThisSession(applicantGuid || '', 'landing');
    if (applicantGuid) {
      scheduleReminder({
        applicantGuid,
        page: 'landing',
        trigger,
      }).catch((error: unknown) => {
        console.error('[VI-548] scheduleReminder failed', error);
      });
    }
    dismissExitIntent();
  };

  // VI-594 resume handlers. Both bypass the terms-acceptance check on
  // purpose: the candidate accepted terms on a previous session (which is
  // how they have answers on file), and we don't want to re-gate them on
  // resume. The interview page reads `resumeFromIndex` from sessionStorage
  // on mount to initialize currentQuestionIndex.
  const navigateIntoInterview = () => {
    setIsNavigating(true);
    if (contentType === 'audio') {
      navigate('/v1/interview/questions/audio');
    } else {
      navigate('/v1/interview/questions');
    }
  };

  const logResumeCtaClick = () => {
    logScreenerCtaClicked({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: applicantGuid || undefined,
      deviceType: getDeviceType(),
      source: getScreenerSource(),
    });
  };

  const handleResumeContinue = () => {
    setWithExpiry('resumeFromIndex', answeredCount, 1000 * 60 * 60 * 24);
    logResumeCtaClick();
    navigateIntoInterview();
  };

  const handleResumeRestart = () => {
    // Soft restart: keep upstream answers (they will be overwritten as the
    // candidate re-records); just reset the local resume pointer to 0.
    setWithExpiry('resumeFromIndex', 0, 1000 * 60 * 60 * 24);
    logResumeCtaClick();
    navigateIntoInterview();
  };

  const handleStartInterview = async () => {
    if (!termsAccepted) {
      setShowTermsError(true);
      setShakeButton(true);
      setTimeout(() => setShakeButton(false), 500);
      setTimeout(() => termsErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      return;
    }
    setShowTermsError(false);

    logScreenerCtaClicked({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: applicantGuid || undefined,
      deviceType: getDeviceType(),
      source: getScreenerSource(),
    });

    await new Promise(resolve => setTimeout(resolve, 250));
    if (ttsLoading || !isConnected) {
      if (!isConnected) {
        setToast({
          hasTitle: true,
          title: 'Connection Lost',
          message: 'You are offline. Please reconnect to continue.',
          type: 'error',
          isMobile,
          isOpen: true,
        });
        setShowToast(true);
      }
      return;
    }

    setIsNavigating(true);
    if (contentType === 'audio') {
      navigate('/v1/interview/questions/audio');
    } else {
      navigate('/v1/interview/questions');
    }
  };

  const handleTermsChange = (accepted: boolean) => {
    setTermsAccepted(accepted);
    if (accepted) setShowTermsError(false);
  };

  const noQuestionnaire = !isLoading && !hasQuestionnaire;

  const RIGHT_PADDING = isMobile ? '1.5rem' : '2rem';

  return (
    <>
      {showToast && (
        <ToastMessage {...toast} isMobile={isMobile} onClose={() => setShowToast(false)} />
      )}

      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        width: '100%',
        maxHeight: (isMobile || isShortViewport) ? 'none' : '780px',
        margin: isMobile ? 0 : '0 auto',
        padding: isMobile ? '0.75rem 0.75rem 5rem' : (isShortViewport ? '1rem 1rem 5rem' : '1rem'),
        background: '#ffffff',
      }}>
        {/* Left column — Hero image (hidden on mobile per design team direction) */}
        {!isMobile && (
          <div style={{
            flex: '0 0 45%',
            background: '#EBF2FF',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            padding: 0,
            borderRadius: '1rem',
            overflow: 'hidden',
          }}>
            <img
              src="/images/corporate-handshake.png"
              alt="Welcome to your interview"
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top center',
                userSelect: 'none',
              }}
            />
          </div>
        )}

        {/* Right column — Content */}
        <div style={{
          flex: isMobile ? 'none' : '1 1 50%',
          padding: isMobile ? '1rem' : `1.25rem ${RIGHT_PADDING} 1rem`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isLargeDesktops ? 'center' : 'flex-start',
          // justifyContent: 'flex-start',
          background: '#ffffff',
        }}>
          {isLoading ? (
            <LandingPageSkeleton isMobile={isMobile} />
            // <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            //   <Spinner size={isMobile ? 'lg' : 'xl'} />
            // </div>
          ) : noQuestionnaire ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              textAlign: 'center',
              padding: '2rem',
            }}>
              <h4 style={{ fontSize: isMobile ? '1rem' : '1.25rem', fontWeight: 600, color: '#333', marginBottom: '1rem', maxWidth: 600, lineHeight: 1.5 }}>
                This employer has removed the one-way interview from the hiring process for this role.
              </h4>
              <p style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#666', margin: 0, maxWidth: 600, lineHeight: 1.6 }}>
                No action is needed at this time.
              </p>
            </div>
          ) : (
          <>
          {/* Title */}
          <h1 style={{
            fontSize: isMobile ? '1.9rem' : '2.1rem',
            fontWeight: 600,
            color: '#1a1a2e',
            lineHeight: 1.25,
            margin: '0 0 0.5rem 0',
          }}>
            Take the Next Step with {!isMobile && <br/>}
            <span style={{ color: '#0045A1'}}>
              {companyName}
            </span>
          </h1>

          {/* Description */}
          <p style={{
            fontSize: isMobile ? '0.95rem' : '1.1rem',
            color: '#495057',
            lineHeight: 1.5,
            margin: '0 0 1.5rem 0',
          }}>
            You&rsquo;re invited to complete a one-way interview for{' '}
            <span
              // onClick={() => job && setShowJobDetails(true)}
              style={{
                fontWeight: 500,
                // cursor: job ? 'pointer' : 'default',
                color: '#0045A1',
              }}
            >
              {jobTitle}
            </span>
            . This is an opportunity for us to learn more about you and your experience.
          </p>

          {/* Stats row */}
          {questionCount > 0 && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: isMobile ? '1rem' : '1.5rem',
              marginBottom: '2rem',
              marginLeft: '0.25rem',
              fontSize: '0.9rem',
              color: '#495057',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fas fa-list" style={{ fontSize: '1.05rem' }}></i>
                Questions: {questionCount}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <img src="/svgs/timer.svg" alt="Timer" style={{ width: '1.05rem', height: '1.05rem', flexShrink: 0 }} />
                Est. Duration: {estimatedDuration} mins
              </span>
            </div>
          )}

          {/* Job info card */}
          {job && (
            <div style={{
              border: '1px solid #DEE2E6',
              borderRadius: '14px',
              padding: '0.85rem 1rem',
              marginBottom: '1.75rem',
              backgroundColor: "#F2F3F7",
            }}>
              <p style={{ margin: '0 0 0.5rem 0' }}>
                <span
                  onClick={() => setShowJobDetails(true)}
                  id="btn-showmore-click"
                  style={{
                    fontWeight: 500,
                    fontSize: '1.3rem',
                    color: '#181C32',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#1E6DBB')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#181C32')}
                >
                  {jobTitle}
                </span>
              </p>
              {companyName && (
                <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666', margin: '0 0 0.5rem 0' }}>
                  <img src="/svgs/company.svg" alt="Company" draggable={false} style={{ width: '1.05rem', height: '1.05rem', flexShrink: 0 }} />
                  {companyName}
                </p>
              )}
              {location && (
                <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#666', margin: 0 }}>
                  <img src="/svgs/marker.svg" alt="Location" draggable={false} style={{ width: '1.05rem', height: '1.05rem', flexShrink: 0 }} />
                  {location}
                </p>
              )}
            </div>
          )}

          {/* Interview Preparation Tips */}
          <button
            id="btn-interviewtips-click"
            onClick={() => setShowTipsModal(true)}
            onMouseEnter={e => {
              e.currentTarget.style.color = '#1E6DBB';
              const textSpan = e.currentTarget.querySelector('span');
              if (textSpan) textSpan.style.textDecoration = 'underline';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = '#495057';
              const textSpan = e.currentTarget.querySelector('span');
              if (textSpan) textSpan.style.textDecoration = 'none';
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              background: 'none',
              border: 'none',
              padding: '0.25rem 0',
              marginBottom: isLargeDesktops ? '9rem' : '6rem',
              marginLeft: '0.25rem',
              color: '#495057',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
              textAlign: 'left',
              alignSelf: 'flex-start',
              transition: 'color 0.2s ease',
            }}
          >
            <i className="far fa-lightbulb" style={{ fontSize: '1.05rem' }}></i>
            <span>Interview Preparation Tips</span>
          </button>

          <TermsCheckbox
            checked={termsAccepted}
            onChange={handleTermsChange}
            legalText={onboardingData.legal}
          />

          <GradientButton
            onClick={handleStartInterview}
            label={onboardingData.buttons.startInterview}
            shake={shakeButton}
            id={`start-interview-${applicantGuid}`}
          />

          <WatchVideoButton
            onClick={() => contentType && setShowExplainerVideo(true)}
            disabled={!contentType}
            id="btn-welcomevideo-click"
          />

          {showTermsError && (
            <div
              ref={termsErrorRef}
              style={{ color: '#802932', fontSize: '0.9rem', marginTop: '0.25rem' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <i className="fal fa-circle-exclamation" style={{ fontSize: '1.2rem', color: '#802932', marginRight: '0.3rem' }}></i>
                Please confirm your acceptance of the terms and conditions by checking the box to continue with the interview.
              </div>
            </div>
          )}
          </>
          )}
        </div>
      </div>

      {/* Modals */}
      {job && (
        <JobDetailsModal
          isOpen={showJobDetails}
          onClose={() => setShowJobDetails(false)}
          job={job}
        />
      )}

      {contentType && (
        <ExplainerVideo
          contentType={contentType as 'video' | 'audio'}
          isOpen={showExplainerVideo}
          onClose={() => setShowExplainerVideo(false)}
          title={`Welcome to your interview with ${companyName}`}
        />
      )}

      <InterviewTipsModal
        isOpen={showTipsModal}
        onClose={() => setShowTipsModal(false)}
        termsAccepted={termsAccepted}
        onTermsChange={handleTermsChange}
        onStartInterview={handleStartInterview}
        onWatchVideo={() => contentType && setShowExplainerVideo(true)}
        tips={onboardingData.interview.tips}
        tipsHeading={onboardingData.interview.tipsHeading}
        legalText={onboardingData.legal}
      />

      <ExitIntentModal
        isOpen={showExitIntentModal}
        title="Complete Your Interview Later?"
        body={`${companyName} is currently reviewing candidates for this role. Start your interview now or get a reminder to complete it later.`}
        primaryLabel="Start Interview"
        secondaryLabel="Send Reminder"
        primaryId="btn-exitintent-start-interview-click"
        secondaryId="btn-exitintent-send-reminder-click"
        onPrimary={handleExitIntentStartInterview}
        onSecondary={handleExitIntentSendReminder}
      />

      <ResumeInterviewModal
        isOpen={showResumeModal}
        completedCount={answeredCount}
        totalCount={questionCount}
        onContinue={handleResumeContinue}
        onRestart={handleResumeRestart}
      />
    </>
  );
};

export default LandingPage;
